const EVENT_QUEUE_KEY = "sonicbox-event-queue";
const IDEMPOTENCY_KEY = "sonicbox-event-idempotency";
const RECENT_LOGIN_KEY = "sonicbox-event-recent-login";
const ACCESS_SESSION_KEY = "sonicbox-event-access";
const SESSION_ID_KEY = "sonicbox-event-session-id";
const STORED_CODES_KEY = "sonicbox-codes";
const REDEEM_SUBMISSIONS_KEY = "sonicbox-redeem-submissions";
const PENDING_REDEEM_KEY = "sonicbox-pending-redeem";
const STORED_USER_KEY = "sonicboxUser";
const STORED_RADIO_KEY = "radio";
const DEFAULT_IDEMPOTENCY_TTL_MS = 24 * 60 * 60 * 1000;
const RECENT_LOGIN_TTL_MS = 30 * 1000;
const REDEEM_SUBMISSION_PENDING_TTL_MS = 2 * 60 * 1000;
const REDEEM_SUBMISSION_SUCCESS_TTL_MS = 5 * 60 * 1000;

const isBrowser = () => typeof window !== "undefined";
const getLocalStorage = () => (isBrowser() ? window.localStorage : null);
const getSessionStorage = () => (isBrowser() ? window.sessionStorage : null);

const readJson = (storage, key, fallback) => {
  if (!storage) return fallback;

  try {
    const raw = storage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (error) {
    console.log(`eventEmitter readJson error for ${key}`, error);
    return fallback;
  }
};

const writeJson = (storage, key, value) => {
  if (!storage) return;

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.log(`eventEmitter writeJson error for ${key}`, error);
  }
};

const removeItem = (storage, key) => {
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch (error) {
    console.log(`eventEmitter removeItem error for ${key}`, error);
  }
};

const createStorageId = (prefix = "evt") => {
  if (isBrowser() && window.crypto?.randomUUID) {
    return `${prefix}_${window.crypto.randomUUID()}`;
  }

  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const pruneIdempotencyEntries = (entries) => {
  if (!entries || typeof entries !== "object") return {};

  const now = Date.now();
  return Object.entries(entries).reduce((accumulator, [key, value]) => {
    if (
      value &&
      typeof value === "object" &&
      (!value.expiresAt || value.expiresAt > now)
    ) {
      accumulator[key] = value;
    }
    return accumulator;
  }, {});
};

export const getEventQueue = () => {
  return readJson(getLocalStorage(), EVENT_QUEUE_KEY, []);
};

export const setEventQueue = (queue) => {
  writeJson(getLocalStorage(), EVENT_QUEUE_KEY, queue);
};

export const enqueueEventDraft = (draft) => {
  const queue = getEventQueue();
  const existingIndex = queue.findIndex(
    (item) =>
      item.eventId === draft.eventId ||
      (!!draft.idempotencyKey && item.idempotencyKey === draft.idempotencyKey)
  );

  if (existingIndex > -1) {
    queue[existingIndex] = draft;
  } else {
    queue.push(draft);
  }

  setEventQueue(queue);
};

export const removeQueuedEvent = (eventId, idempotencyKey = null) => {
  const queue = getEventQueue().filter(
    (item) =>
      item.eventId !== eventId &&
      (!idempotencyKey || item.idempotencyKey !== idempotencyKey)
  );
  setEventQueue(queue);
};

export const getIdempotencyEntries = () => {
  const storage = getLocalStorage();
  const entries = pruneIdempotencyEntries(
    readJson(storage, IDEMPOTENCY_KEY, {})
  );

  writeJson(storage, IDEMPOTENCY_KEY, entries);
  return entries;
};

export const getIdempotencyEntry = (idempotencyKey) => {
  if (!idempotencyKey) return null;
  return getIdempotencyEntries()[idempotencyKey] ?? null;
};

export const setIdempotencyEntry = (
  idempotencyKey,
  entry,
  ttlMs = DEFAULT_IDEMPOTENCY_TTL_MS
) => {
  if (!idempotencyKey || !entry) return null;

  const storage = getLocalStorage();
  const entries = getIdempotencyEntries();
  const nextEntry = {
    ...entry,
    expiresAt: Date.now() + ttlMs,
  };

  entries[idempotencyKey] = nextEntry;
  writeJson(storage, IDEMPOTENCY_KEY, entries);
  return nextEntry;
};

export const removeIdempotencyEntry = (idempotencyKey) => {
  if (!idempotencyKey) return;

  const entries = getIdempotencyEntries();
  delete entries[idempotencyKey];
  writeJson(getLocalStorage(), IDEMPOTENCY_KEY, entries);
};

export const getBrowserSessionId = () => {
  const sessionStorage = getSessionStorage();
  if (!sessionStorage) return null;

  const existingSessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (existingSessionId) {
    return existingSessionId;
  }

  const nextSessionId = createStorageId("sess");
  sessionStorage.setItem(SESSION_ID_KEY, nextSessionId);
  return nextSessionId;
};

export const normalizeStoredRedeemCode = (entry) => {
  if (!entry) return null;

  if (typeof entry === "string") {
    return {
      encodedCode: entry,
      source: "legacy",
      sourceDetail: null,
      capturedAt: null,
      radioHint: null,
    };
  }

  if (typeof entry !== "object") return null;

  const encodedCode = entry.encodedCode ?? entry.code ?? entry.value ?? null;
  if (!encodedCode) return null;

  return {
    encodedCode,
    source: entry.source ?? "legacy",
    sourceDetail: entry.sourceDetail ?? null,
    capturedAt: entry.capturedAt ?? null,
    radioHint: entry.radioHint ?? null,
  };
};

export const getStoredRedeemCodes = () => {
  const storedCodes = readJson(window.localStorage, STORED_CODES_KEY, []);

  if (!Array.isArray(storedCodes)) return [];

  return storedCodes
    .map((entry) => normalizeStoredRedeemCode(entry))
    .filter(Boolean);
};

export const setStoredRedeemCodes = (codes) => {
  const normalizedCodes = codes
    .map((entry) => normalizeStoredRedeemCode(entry))
    .filter(Boolean);

  writeJson(getLocalStorage(), STORED_CODES_KEY, normalizedCodes);
};

export const upsertStoredRedeemCode = (entry) => {
  const normalizedEntry = normalizeStoredRedeemCode(entry);
  if (!normalizedEntry) return null;

  const codes = getStoredRedeemCodes();
  const existingIndex = codes.findIndex(
    (item) => item.encodedCode === normalizedEntry.encodedCode
  );

  if (existingIndex > -1) {
    codes[existingIndex] = {
      ...codes[existingIndex],
      ...normalizedEntry,
      radioHint: normalizedEntry.radioHint ?? codes[existingIndex].radioHint,
      capturedAt: normalizedEntry.capturedAt ?? codes[existingIndex].capturedAt,
    };
  } else {
    codes.push(normalizedEntry);
  }

  setStoredRedeemCodes(codes);
  return normalizedEntry;
};

export const removeStoredRedeemCode = (entryOrCode) => {
  const encodedCode =
    typeof entryOrCode === "string"
      ? entryOrCode
      : normalizeStoredRedeemCode(entryOrCode)?.encodedCode;

  if (!encodedCode) return;

  const codes = getStoredRedeemCodes().filter(
    (item) => item.encodedCode !== encodedCode
  );

  setStoredRedeemCodes(codes);
};

const pruneRedeemSubmissions = (entries) => {
  if (!entries || typeof entries !== "object") return {};

  const currentTime = Date.now();
  return Object.entries(entries).reduce((accumulator, [key, entry]) => {
    if (entry?.expiresAt && entry.expiresAt > currentTime) {
      accumulator[key] = entry;
    }
    return accumulator;
  }, {});
};

const getRedeemSubmissionEntries = () => {
  const storage = getLocalStorage();
  const entries = pruneRedeemSubmissions(
    readJson(storage, REDEEM_SUBMISSIONS_KEY, {})
  );

  writeJson(storage, REDEEM_SUBMISSIONS_KEY, entries);
  return entries;
};

const setRedeemSubmissionEntries = (entries) => {
  writeJson(getLocalStorage(), REDEEM_SUBMISSIONS_KEY, entries);
};

export const getRedeemSubmission = (encodedCode) => {
  if (!encodedCode) return null;
  return getRedeemSubmissionEntries()[encodedCode] ?? null;
};

export const acquireRedeemSubmission = ({ encodedCode, code = null } = {}) => {
  if (!encodedCode) {
    return { acquired: true, entry: null };
  }

  const entries = getRedeemSubmissionEntries();
  const existingEntry = entries[encodedCode];

  if (
    existingEntry &&
    ["pending", "success"].includes(existingEntry.status)
  ) {
    return {
      acquired: false,
      entry: existingEntry,
    };
  }

  const currentTime = Date.now();
  const entry = {
    encodedCode,
    code,
    status: "pending",
    startedAt: currentTime,
    expiresAt: currentTime + REDEEM_SUBMISSION_PENDING_TTL_MS,
  };

  entries[encodedCode] = entry;
  setRedeemSubmissionEntries(entries);

  return {
    acquired: true,
    entry,
  };
};

export const markRedeemSubmissionSuccess = (encodedCode, result = null) => {
  if (!encodedCode) return null;

  const entries = getRedeemSubmissionEntries();
  const currentTime = Date.now();
  const entry = {
    ...(entries[encodedCode] ?? {}),
    encodedCode,
    status: "success",
    result,
    completedAt: currentTime,
    expiresAt: currentTime + REDEEM_SUBMISSION_SUCCESS_TTL_MS,
  };

  entries[encodedCode] = entry;
  setRedeemSubmissionEntries(entries);
  return entry;
};

export const releaseRedeemSubmission = (encodedCode) => {
  if (!encodedCode) return;

  const entries = getRedeemSubmissionEntries();
  delete entries[encodedCode];
  setRedeemSubmissionEntries(entries);
};

export const getStoredUser = () => {
  return readJson(getLocalStorage(), STORED_USER_KEY, null);
};

export const getStoredRadioInfo = () => {
  return readJson(getLocalStorage(), STORED_RADIO_KEY, null);
};

export const getRedirectLoginPath = () => {
  if (!isBrowser()) return null;

  try {
    return window.localStorage.getItem("redirectLogin");
  } catch (error) {
    console.log("eventEmitter getRedirectLoginPath error", error);
    return null;
  }
};

export const getPendingRedeemContext = () => {
  const pendingContext = readJson(getLocalStorage(), PENDING_REDEEM_KEY, null);
  if (!pendingContext) return null;

  return {
    ...pendingContext,
    radioHint: pendingContext.radioHint ?? null,
  };
};

export const setPendingRedeemContext = (pendingContext) => {
  if (!pendingContext) return;
  writeJson(getLocalStorage(), PENDING_REDEEM_KEY, pendingContext);
};

export const updatePendingRedeemContext = (patch) => {
  const current = getPendingRedeemContext() ?? {};
  setPendingRedeemContext({
    ...current,
    ...patch,
    radioHint: {
      ...(current.radioHint ?? {}),
      ...(patch?.radioHint ?? {}),
    },
  });
};

export const clearPendingRedeemContext = () => {
  removeItem(getLocalStorage(), PENDING_REDEEM_KEY);
};

export const markRecentInteractiveLogin = (userSnapshot = {}) => {
  writeJson(getSessionStorage(), RECENT_LOGIN_KEY, {
    at: Date.now(),
    userid: userSnapshot?.userid ?? userSnapshot?.id ?? null,
  });
};

export const consumeRecentInteractiveLogin = () => {
  const sessionStorage = getSessionStorage();
  const marker = readJson(sessionStorage, RECENT_LOGIN_KEY, null);

  if (!marker?.at) {
    return false;
  }

  removeItem(sessionStorage, RECENT_LOGIN_KEY);
  return Date.now() - marker.at <= RECENT_LOGIN_TTL_MS;
};

export const hasSeenAccess = (userid, radioid) => {
  if (!userid || !radioid) return false;

  const seenMap = readJson(getSessionStorage(), ACCESS_SESSION_KEY, {});
  return !!seenMap[`${userid}:${radioid}`];
};

export const markAccessSeen = (userid, radioid) => {
  if (!userid || !radioid) return;

  const sessionStorage = getSessionStorage();
  const seenMap = readJson(sessionStorage, ACCESS_SESSION_KEY, {});
  seenMap[`${userid}:${radioid}`] = Date.now();
  writeJson(sessionStorage, ACCESS_SESSION_KEY, seenMap);
};
